import { feimaChatChatPackageUsage } from '@/api/packageApi';
import { create } from 'zustand';
import type {
  McpToolOption,
  SupportedChatModelConfigResult,
  SessionToolsResult,
  ChatFileDTO,
} from '@/api/sessionApi';

type SelectedFilesUpdater =
  | ChatFileDTO[]
  | ((previousFiles: ChatFileDTO[]) => ChatFileDTO[]);

interface ChatStore {
  isLoading: Boolean;
  fetchControllers: any[];
  qualification: any;
  chatPackage: API.FeimaChatChatPackageUsageResponse['data'];

  /** 当前会话的工具选项（getTools 返回结果） */
  sessionTools: SessionToolsResult | null;
  /** 选中的 MCP 工具配置 ID（null = 不使用） */
  selectedMcpConfigId: number | null;
  /** 选用的模型 ID（空串 = 使用机器人默认） */
  selectedModel: string;
  /** 是否开启深度思考 */
  thinkingEnabled: boolean;
  /** 从其他页面跳转携带的预设选择，SessionInput 拉完工具后应用一次然后清除 */
  pendingToolSelection: {
    model: string;
    mcpConfigId: number | null;
    thinkingEnabled: boolean;
    skillIds?: string[];
  } | null;
  /** 当前待发送的附件列表 */
  selectedFiles: ChatFileDTO[];
  /** 当前待发送的 skill ID 列表 */
  selectedSkillIds: string[];
  /** 是否开启视频生成（与 MCP 工具互斥） */
  videoGenerationEnabled: boolean;

  updateLoadingStatus: (value: boolean) => void;
  addController: (value: any) => void;
  controllerAbort: () => void;
  updateQualification: (value: any) => void;
  updateChatPackage: () => void;

  setSessionTools: (tools: SessionToolsResult | null) => void;
  setSelectedMcpConfigId: (id: number | null) => void;
  setSelectedModel: (
    model: string,
    options?: { preserveFiles?: boolean }
  ) => void;
  setThinkingEnabled: (enabled: boolean) => void;
  resetToolSelection: () => void;
  setPendingToolSelection: (
    selection: {
      model: string;
      mcpConfigId: number | null;
      thinkingEnabled: boolean;
      skillIds?: string[];
    } | null
  ) => void;
  setSelectedFiles: (files: SelectedFilesUpdater) => void;
  setSelectedSkillIds: (skillIds: string[]) => void;
  clearSelectedFiles: () => void;
  setVideoGenerationEnabled: (enabled: boolean) => void;
}

export const useChatStore = create<ChatStore>((set, get) => ({
  // 对话loading
  isLoading: false,
  // 对话中断控制器
  fetchControllers: [],
  // 对话资格数据
  qualification: {},
  // 套餐用量
  chatPackage: {} as API.FeimaChatChatPackageUsageResponse['data'],

  // 工具选项
  sessionTools: null,
  selectedMcpConfigId: null,
  selectedModel: '',
  thinkingEnabled: false,
  pendingToolSelection: null,
  selectedFiles: [],
  selectedSkillIds: [],
  videoGenerationEnabled: false,

  updateLoadingStatus(isLoading: boolean) {
    set({ isLoading });
  },

  addController(controller: any) {
    const newList = [...get().fetchControllers].concat(controller);
    set({ fetchControllers: newList });
  },

  controllerAbort() {
    const list = [...get().fetchControllers];
    list.forEach(controller => controller?.abort());
    set({ fetchControllers: [] });
  },

  updateQualification(qualification: any) {
    set({ qualification });
  },

  updateChatPackage() {
    // 遮蔽，暂时不加会员 20250206
    // feimaChatChatPackageUsage().then(res => {
    //   set({ chatPackage: res });
    // });
  },

  setSessionTools(tools) {
    set({ sessionTools: tools });
  },

  setSelectedMcpConfigId(id) {
    set({ selectedMcpConfigId: id });
  },

  setSelectedModel(model, options) {
    // 切换模型时，若新模型不支持深度思考则关闭
    const tools = get().sessionTools;
    const targetModel = tools?.models?.find(m => m.modelId === model);
    set({
      selectedModel: model,
      thinkingEnabled: targetModel?.supportsThinking
        ? get().thinkingEnabled
        : false,
      ...(options?.preserveFiles ? {} : { selectedFiles: [] }),
    });
  },

  setThinkingEnabled(enabled) {
    set({ thinkingEnabled: enabled });
  },

  resetToolSelection() {
    set({
      sessionTools: null,
      selectedMcpConfigId: null,
      selectedModel: '',
      thinkingEnabled: false,
      selectedSkillIds: [],
      videoGenerationEnabled: false,
    });
  },

  setPendingToolSelection(selection) {
    set({ pendingToolSelection: selection });
  },

  setSelectedFiles(files) {
    set(state => ({
      selectedFiles:
        typeof files === 'function' ? files(state.selectedFiles) : files,
    }));
  },

  setSelectedSkillIds(skillIds) {
    set({ selectedSkillIds: skillIds });
  },

  clearSelectedFiles() {
    set({ selectedFiles: [] });
  },

  setVideoGenerationEnabled(enabled) {
    set({ videoGenerationEnabled: enabled });
  },
}));
